import RBush from 'rbush';
import * as React from 'react';

import { ClickHandlers } from './ClickHandlers';
import { DetailsVisibilityManager } from './DetailsVisibilityManager';
import { DotActive } from './DotActive';
import { DotHover } from './DotHover';
import { FavoritesUpdater } from './FavoritesUpdater';
import { FeaturesPropertiesUpdater } from './FeaturesPropertiesUpdater';
import { FeaturesUpdater } from './FeaturesUpdater';
import { OffersCounter } from './OffersCounter';
import { ResultabilityManager } from './ResultabilityManager';
import { SubdomainUpdater } from './SubdomainUpdater';
import { TestMode } from './TestMode';
import { FeaturesContext, IFeaturesContext } from './context';
import { IFrecklesRBushItem } from '../../../../../common/types/freckles';
import { IRBushItem } from '../../../../../common/types/map';
import { useMapContext } from '../../context';

export interface IFeaturesApi {
  init(): void;
  destroy(): void;
}

export const Features = React.forwardRef((_, ref) => {
  const { ymaps, map } = useMapContext();

  const rbushRef = React.useRef(new RBush<IRBushItem>());
  const rbushFrecklesRef = React.useRef(new RBush<IFrecklesRBushItem>());
  const pointsLayerRef = React.useRef(new ymaps.PointsLayer({ map, rbush: rbushRef.current }));
  const frecklesPointsLayerRef = React.useRef(new ymaps.FrecklesPointsLayer({ map, rbush: rbushFrecklesRef.current }));
  const hotspotsLayerRef = React.useRef(new ymaps.HotspotsLayer({ map, rbush: rbushFrecklesRef.current }));
  const balloonManagerRef = React.useRef(new ymaps.BalloonManager({ map, rbush: rbushRef.current }));
  const [isDetailsVisible, changeIsDetailsVisible] = React.useState(false);
  const [isResultsAvailable, changeIsResultsAvailable] = React.useState(false);

  const handleVisibilityChange = React.useCallback((isDetailsVisible: boolean) => {
    changeIsDetailsVisible(isDetailsVisible);
  }, []);

  const handleResultabilityChange = React.useCallback((isResultable: boolean) => {
    changeIsResultsAvailable(isResultable);
  }, []);

  React.useImperativeHandle(
    ref,
    () => ({
      init: () => {
        map.layers.add(pointsLayerRef.current.layer);
        map.layers.add(frecklesPointsLayerRef.current.layer);
        map.layers.add(hotspotsLayerRef.current.layer);
        map.geoObjects.add(balloonManagerRef.current.objectManager);
      },
      destroy: () => {
        map.layers.remove(pointsLayerRef.current.layer);
        map.layers.remove(frecklesPointsLayerRef.current.layer);
        map.layers.remove(hotspotsLayerRef.current.layer);
        map.geoObjects.remove(balloonManagerRef.current.objectManager);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const context = React.useMemo<IFeaturesContext>(
    () => ({
      rbush: rbushRef.current,
      rbushFreckles: rbushFrecklesRef.current,
      pointsLayer: pointsLayerRef.current,
      hotspotsLayer: hotspotsLayerRef.current,
      balloonManager: balloonManagerRef.current,
      frecklesPointsLayer: frecklesPointsLayerRef.current,
    }),
    [],
  );

  return (
    <FeaturesContext.Provider value={context}>
      <ClickHandlers resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <DetailsVisibilityManager onVisibilityChange={handleVisibilityChange} />
      <DotActive resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <DotHover resultsAvailable={isResultsAvailable} detailsVisible={isDetailsVisible} />
      <FavoritesUpdater />
      <FeaturesPropertiesUpdater />
      <FeaturesUpdater detailsVisible={isDetailsVisible} />
      <ResultabilityManager onResultabilityChange={handleResultabilityChange} />
      <SubdomainUpdater />
      <TestMode map={map} />
      <OffersCounter />
    </FeaturesContext.Provider>
  );
});

Features.displayName = 'Features';
